import { PROTOCOL_VERSION } from "./constants.js";

export type GameMode = "CHAIN" | "PUZZLE" | "RUSH";

export type ClientAction =
  | "AUTH"
  | "MATCH_REQ"
  | "CANCEL_MATCH"
  | "CREATE_ROOM"
  | "JOIN_ROOM"
  | "LEAVE_ROOM"
  | "READY"
  | "QUERY_CANDIDATES"
  | "SUBMIT_WORD"
  | "USE_HINT"
  | "USE_ITEM"
  | "SURRENDER"
  | "RECONNECT"
  | "REMATCH"
  | "HEARTBEAT";

export type ServerAction =
  | "AUTH_OK"
  | "ROOM_CREATED"
  | "ROOM_LOBBY"
  | "MATCH_SUCCESS"
  | "ROUND_START"
  | "CANDIDATES"
  | "HINT_RESULT"
  | "ITEM_APPLIED"
  | "ACTION_RESULT"
  | "PUNISHMENT"
  | "GAME_OVER"
  | "ROOM_SNAPSHOT"
  | "OPPONENT_OFFLINE"
  | "OPPONENT_ONLINE"
  | "MATCH_TIMEOUT"
  | "SERVER_SHUTDOWN"
  | "ERROR"
  | "PONG";

export type Envelope<A extends string, P> = {
  v: typeof PROTOCOL_VERSION;
  action: A;
  seq: number;
  ts: number;
  payload: P;
  ack?: number;
};

export type AuthPayload = {
  code: string;
  nickname: string;
  avatar: string;
};

export type MatchReqPayload = { mode: GameMode; maxSeats?: number };
export type CancelMatchPayload = Record<string, never>;
export type CreateRoomPayload = { mode: GameMode; maxSeats?: number };
export type JoinRoomPayload = { roomCode: string };
export type LeaveRoomPayload = { roomId?: string };
export type ReadyPayload = { ready: boolean };
export type QueryCandidatesPayload = { roomId: string; round: number; abbr: string };
export type SubmitWordPayload = { roomId: string; round: number; word: string };
export type UseHintPayload = { roomId: string; round: number };
export type ItemId = "SCRAMBLE";
export type UseItemPayload = { roomId: string; round: number; itemId: ItemId };
export type SurrenderPayload = { roomId: string };
export type ReconnectPayload = { token: string; roomId: string };
export type RematchPayload = { roomId: string };
export type HeartbeatPayload = Record<string, never>;

export type ClientPayloadMap = {
  AUTH: AuthPayload;
  MATCH_REQ: MatchReqPayload;
  CANCEL_MATCH: CancelMatchPayload;
  CREATE_ROOM: CreateRoomPayload;
  JOIN_ROOM: JoinRoomPayload;
  LEAVE_ROOM: LeaveRoomPayload;
  READY: ReadyPayload;
  QUERY_CANDIDATES: QueryCandidatesPayload;
  SUBMIT_WORD: SubmitWordPayload;
  USE_HINT: UseHintPayload;
  USE_ITEM: UseItemPayload;
  SURRENDER: SurrenderPayload;
  RECONNECT: ReconnectPayload;
  REMATCH: RematchPayload;
  HEARTBEAT: HeartbeatPayload;
};

export type ClientMessage<A extends ClientAction = ClientAction> = Envelope<A, ClientPayloadMap[A]>;

export type PublicPlayer = {
  id: string;
  nickname: string;
  avatar: string;
};

export type AuthOkPayload = { userId: string; token: string; serverTime: number; wins: number };
export type RoomCreatedPayload = { roomCode: string; shareTicket: string; roomId: string };
export type MatchSuccessPayload = {
  roomId: string;
  mode: GameMode;
  opponent?: PublicPlayer;
  players?: PublicPlayer[];
  firstPlayerId: string;
  initialHp: number;
  hintsLeft: number;
  scrambleItemsLeft?: number;
  matchDeadline?: number;
  scores?: Record<string, number>;
  roomCode?: string;
};

export type LobbySeat = PublicPlayer & { ready: boolean; online: boolean };

export type RoomLobbyPayload = {
  roomId: string;
  roomCode: string;
  hostId: string;
  mode: GameMode;
  seats: LobbySeat[];
  minSeats: number;
  maxSeats: number;
  countdownDeadline: number | null;
  readyDeadline: number | null;
  publicMatch: boolean;
  serverTime: number;
};

export type RoundStartReason = "NORMAL" | "DEAD_END_RESET" | "BOTH_STUCK_RESET" | "GAME_START";

export type ScrambleEffect = {
  targetId: string;
  seed: number;
  turnsLeft: number;
};

export type RoundStartPayload = {
  round: number;
  currentTurnId: string;
  lastWord: string;
  lastPinyins: string[];
  deadline: number;
  serverTime: number;
  turnMs: number;
  reason: RoundStartReason;
  scrambles?: ScrambleEffect[];
};

export type CandidatesPayload = { round: number; abbr: string; words: string[] };
export type HintResultPayload = { round: number; word: string; hintsLeft: number };
export type ItemAppliedPayload = {
  itemId: ItemId;
  fromId: string;
  targetId: string;
  seed: number;
  turnsLeft: number;
  itemsLeft: number;
};

export type RejectReason = "INVALID_FORMAT" | "NOT_IN_DICT" | "ALREADY_USED" | "WRONG_PINYIN";

export type ActionResultPayload = {
  success: boolean;
  playerId: string;
  word: string;
  round: number;
  reason: RejectReason | null;
  scores?: Record<string, number>;
};

export type PunishmentReason = "TIMEOUT" | "MISTAKE";

export type PunishmentPayload = {
  targetId: string;
  hpLeft: number;
  punishLevel: number;
  reason: PunishmentReason;
};

export type GameOverReason = "HP_ZERO" | "SURRENDER" | "DISCONNECT" | "MAX_ROUNDS" | "TIME_UP";

export type RushRankRow = {
  userId: string;
  nickname: string;
  score: number;
  rank: number;
};

export type PlayerStats = {
  correct: number;
  mistakes: number;
  timeouts: number;
  maxCombo: number;
  avgResponseMs: number;
};

export type GameOverPayload = {
  winnerId: string | null;
  reason: GameOverReason;
  durationMs: number;
  totalRounds: number;
  wordChain: string[];
  stats: Record<string, PlayerStats>;
  ranking?: RushRankRow[];
  /** 本局给各玩家计入的胜场增量（非 1v1 匹配每天首次可为 2）。 */
  winDelta?: Record<string, number>;
};

export type SnapshotScramble = { seed: number; turnsLeft: number };
export type SnapshotPlayerYou = {
  userId: string;
  hp: number;
  hintsLeft: number;
  scrambleItemsLeft?: number;
  scramble?: SnapshotScramble;
  score?: number;
};
export type SnapshotPlayerOpp = {
  userId: string;
  nickname: string;
  avatar: string;
  hp: number;
  online: boolean;
  scramble?: SnapshotScramble;
  score?: number;
};

export type SnapshotRushPeer = {
  userId: string;
  nickname: string;
  avatar: string;
  score: number;
  online: boolean;
  racing: boolean;
};

export type RoomSnapshotPayload = {
  roomId: string;
  mode: GameMode;
  state: "CREATED" | "PLAYING" | "ENDED";
  round: number;
  you: SnapshotPlayerYou;
  opponent?: SnapshotPlayerOpp;
  players?: SnapshotRushPeer[];
  currentTurnId: string;
  lastWord: string;
  lastPinyins: string[];
  deadline: number;
  serverTime: number;
  turnMs: number;
  usedWords: string[];
  wordChain: string[];
  matchDeadline?: number;
  scores?: Record<string, number>;
};

export type OpponentOfflinePayload = { opponentId: string; graceDeadline: number };
export type OpponentOnlinePayload = { opponentId: string };
export type MatchTimeoutPayload = Record<string, never>;
export type ServerShutdownPayload = { message: string };
export type ErrorPayload = { code: number; message: string };
export type PongPayload = { serverTime: number };

export type ServerPayloadMap = {
  AUTH_OK: AuthOkPayload;
  ROOM_CREATED: RoomCreatedPayload;
  ROOM_LOBBY: RoomLobbyPayload;
  MATCH_SUCCESS: MatchSuccessPayload;
  ROUND_START: RoundStartPayload;
  CANDIDATES: CandidatesPayload;
  HINT_RESULT: HintResultPayload;
  ITEM_APPLIED: ItemAppliedPayload;
  ACTION_RESULT: ActionResultPayload;
  PUNISHMENT: PunishmentPayload;
  GAME_OVER: GameOverPayload;
  ROOM_SNAPSHOT: RoomSnapshotPayload;
  OPPONENT_OFFLINE: OpponentOfflinePayload;
  OPPONENT_ONLINE: OpponentOnlinePayload;
  MATCH_TIMEOUT: MatchTimeoutPayload;
  SERVER_SHUTDOWN: ServerShutdownPayload;
  ERROR: ErrorPayload;
  PONG: PongPayload;
};

export type ServerMessage<A extends ServerAction = ServerAction> = Envelope<A, ServerPayloadMap[A]> & {
  ack?: number;
};

export function makeEnvelope<A extends string, P>(
  action: A,
  payload: P,
  seq: number,
  ts: number,
  ack?: number,
): Envelope<A, P> {
  const env: Envelope<A, P> = { v: PROTOCOL_VERSION, action, seq, ts, payload };
  if (ack !== undefined) env.ack = ack;
  return env;
}

export function parseEnvelope(raw: string): Envelope<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj["v"] !== "number") return null;
    if (typeof obj["action"] !== "string") return null;
    if (typeof obj["seq"] !== "number") return null;
    if (typeof obj["ts"] !== "number") return null;
    if (typeof obj["payload"] !== "object" || obj["payload"] === null) return null;
    return parsed as Envelope<string, unknown>;
  } catch {
    return null;
  }
}

export const CLIENT_ACTIONS = new Set<string>([
  "AUTH",
  "MATCH_REQ",
  "CANCEL_MATCH",
  "CREATE_ROOM",
  "JOIN_ROOM",
  "LEAVE_ROOM",
  "READY",
  "QUERY_CANDIDATES",
  "SUBMIT_WORD",
  "USE_HINT",
  "USE_ITEM",
  "SURRENDER",
  "RECONNECT",
  "REMATCH",
  "HEARTBEAT",
]);
